import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../components/layout/Sidebar";
import toast from "react-hot-toast";
import api from "../../utils/api";

const Upload = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    invoiceNumber: "", title: "", vendorName: "", vendorTin: "",
    vendorEmail: "", vendorPhone: "", subtotal: "", vatAmount: "",
    totalAmount: "", currency: "GHS", vatRate: "15",
    invoiceDate: "", dueDate: "", status: "pending", type: "purchase",
    category: "", tags: "", isVatInvoice: "true", notes: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const updated = { ...prev, [name]: value };
      // Auto-calculate VAT when subtotal or vatRate changes
      if (name === "subtotal" || name === "vatRate") {
        const sub = parseFloat(name === "subtotal" ? value : prev.subtotal) || 0;
        const rate = parseFloat(name === "vatRate" ? value : prev.vatRate) || 0;
        const vat = parseFloat((sub * rate / 100).toFixed(2));
        updated.vatAmount = vat.toString();
        updated.totalAmount = (sub + vat).toFixed(2);
      }
      return updated;
    });
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) validateAndSetFile(dropped);
  };

  const validateAndSetFile = (f) => {
    const allowed = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
    if (!allowed.includes(f.type)) { toast.error("Only PDF, JPG, PNG allowed"); return; }
    if (f.size > 10 * 1024 * 1024)  { toast.error("File must be under 10MB"); return; }
    setFile(f);
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  if (!file) { toast.error("Please attach an invoice file"); return; }
  if (!form.vendorName) { toast.error("Vendor name is required"); return; }
  if (!form.subtotal) { toast.error("Subtotal is required"); return; }
  if (!form.invoiceDate) { toast.error("Invoice date is required"); return; }

  setLoading(true);
  try {
    const formData = new FormData();
    Object.entries(form).forEach(([k, v]) => formData.append(k, v));
    formData.append("invoice", file);

    await api.post("/invoices", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    toast.success("Invoice uploaded successfully!");
    navigate("/invoices");
    } catch (err) {
    toast.error(err.response?.data?.message || "Upload failed");
    } finally {
    setLoading(false);
    }
  } ;

  return (
    <div style={styles.shell}>
      <Sidebar />
      <main style={styles.main}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.pageTitle}>Upload Invoice</h1>
            <p style={styles.pageSub}>Attach a file and fill in the invoice details</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={styles.layout}>
          {/* Left — File + Notes */}
          <div style={styles.leftCol}>

            {/* Drop Zone */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>Invoice File *</div>
              <div
                style={{ ...styles.dropZone, ...(dragging ? styles.dropZoneActive : {}) }}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleFileDrop}
                onClick={() => document.getElementById("fileInput").click()}
              >
                {file ? (
                  <div style={styles.fileSelected}>
                    <div style={styles.fileIconBig}>{file.type.includes("pdf") ? "📄" : "🖼"}</div>
                    <div style={styles.fileName}>{file.name}</div>
                    <div style={styles.fileSize}>{(file.size / 1024).toFixed(0)} KB</div>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); }} style={styles.removeFile}>Remove</button>
                  </div>
                ) : (
                  <>
                    <div style={styles.dropIconBig}>↑</div>
                    <div style={styles.dropText}>Drag & drop or click to upload</div>
                    <div style={styles.dropSub}>PDF, JPG, PNG · Max 10MB</div>
                  </>
                )}
                <input id="fileInput" type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: "none" }} onChange={(e) => validateAndSetFile(e.target.files[0])} />
              </div>
            </div>

            {/* Notes */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>Notes</div>
              <textarea style={styles.textarea} name="notes" placeholder="Any additional notes about this invoice..." value={form.notes} onChange={handleChange} rows={4} />
            </div>

            {/* Tags */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>Tags</div>
              <input style={styles.input} name="tags" placeholder="e.g. q1, materials, accra (comma separated)" value={form.tags} onChange={handleChange} />
              <p style={styles.inputHint}>Separate tags with commas</p>
            </div>
          </div>

          {/* Right — Invoice Details */}
          <div style={styles.rightCol}>

            {/* Basic Info */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>Invoice Details</div>
              <div style={styles.formGrid}>
                <div style={styles.field}>
                  <label style={styles.label}>Invoice Number</label>
                  <input style={styles.input} name="invoiceNumber" placeholder="INV-0042" value={form.invoiceNumber} onChange={handleChange} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Title</label>
                  <input style={styles.input} name="title" placeholder="Brief description" value={form.title} onChange={handleChange} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Invoice Date *</label>
                  <input style={styles.input} type="date" name="invoiceDate" value={form.invoiceDate} onChange={handleChange} required />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Due Date</label>
                  <input style={styles.input} type="date" name="dueDate" value={form.dueDate} onChange={handleChange} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Type</label>
                  <select style={styles.input} name="type" value={form.type} onChange={handleChange}>
                    {["purchase","sales","expense","utility","payroll","other"].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Status</label>
                  <select style={styles.input} name="status" value={form.status} onChange={handleChange}>
                    {["pending","paid","overdue","disputed","cancelled"].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Category</label>
                  <input style={styles.input} name="category" placeholder="e.g. Office, Construction" value={form.category} onChange={handleChange} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>VAT Invoice?</label>
                  <select style={styles.input} name="isVatInvoice" value={form.isVatInvoice} onChange={handleChange}>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Vendor */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>Vendor Details</div>
              <div style={styles.formGrid}>
                <div style={{ ...styles.field, gridColumn: "span 2" }}>
                  <label style={styles.label}>Vendor Name *</label>
                  <input style={styles.input} name="vendorName" placeholder="Supplier company name" value={form.vendorName} onChange={handleChange} required />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Vendor TIN</label>
                  <input style={styles.input} name="vendorTin" placeholder="C0023456789" value={form.vendorTin} onChange={handleChange} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Vendor Email</label>
                  <input style={styles.input} type="email" name="vendorEmail" placeholder="vendor@company.com" value={form.vendorEmail} onChange={handleChange} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Vendor Phone</label>
                  <input style={styles.input} name="vendorPhone" placeholder="+233 24 123 4567" value={form.vendorPhone} onChange={handleChange} />
                </div>
              </div>
            </div>

            {/* Financials */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>Financials</div>
              <div style={styles.formGrid}>
                <div style={styles.field}>
                  <label style={styles.label}>Currency</label>
                  <select style={styles.input} name="currency" value={form.currency} onChange={handleChange}>
                    {["GHS","USD","EUR","GBP"].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>VAT Rate (%)</label>
                  <select style={styles.input} name="vatRate" value={form.vatRate} onChange={handleChange}>
                    {["0","3","15"].map(r => <option key={r} value={r}>{r}%</option>)}
                  </select>
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Subtotal *</label>
                  <input style={styles.input} type="number" name="subtotal" placeholder="0.00" value={form.subtotal} onChange={handleChange} required />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>VAT Amount</label>
                  <input style={{ ...styles.input, background: "#f7f8fc" }} type="number" name="vatAmount" placeholder="Auto-calculated" value={form.vatAmount} readOnly />
                </div>
                <div style={{ ...styles.field, gridColumn: "span 2" }}>
                  <label style={styles.label}>Total Amount</label>
                  <input style={{ ...styles.input, background: "#f7f8fc", fontWeight: "bold", fontSize: "16px" }} type="number" name="totalAmount" placeholder="Auto-calculated" value={form.totalAmount} readOnly />
                </div>
              </div>
            </div>

            {/* Submit */}
            <div style={styles.submitRow}>
              <button type="button" onClick={() => navigate("/invoices")} style={styles.cancelBtn}>Cancel</button>
              <button type="submit" style={loading ? { ...styles.submitBtn, opacity: 0.7 } : styles.submitBtn} disabled={loading}>
                {loading ? "Uploading..." : "↑ Upload Invoice"}
              </button>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
};

const styles = {
  shell: { display: "flex", minHeight: "100vh", background: "#f7f8fc", fontFamily: "sans-serif" },
  main: { flex: 1, padding: "36px 40px", overflowY: "auto" },
  header: { marginBottom: "28px" },
  pageTitle: { fontSize: "24px", fontWeight: "bold", color: "#0a0f1e", marginBottom: "4px" },
  pageSub: { fontSize: "13px", color: "#718096" },
  layout: { display: "grid", gridTemplateColumns: "1fr 1.6fr", gap: "20px", alignItems: "start" },
  leftCol: { display: "flex", flexDirection: "column", gap: "20px" },
  rightCol: { display: "flex", flexDirection: "column", gap: "20px" },
  card: { background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", padding: "24px" },
  cardTitle: { fontSize: "12px", fontWeight: "bold", color: "#0a0f1e", marginBottom: "16px", textTransform: "uppercase", letterSpacing: "0.5px" },
  dropZone: { border: "2px dashed #e2e8f0", borderRadius: "10px", padding: "36px 20px", textAlign: "center", cursor: "pointer", background: "#fafafa", transition: "all 0.2s" },
  dropZoneActive: { borderColor: "#f6ad55", background: "#fffbeb" },
  dropIconBig: { fontSize: "36px", color: "#cbd5e0", marginBottom: "10px" },
  dropText: { fontSize: "14px", fontWeight: "600", color: "#4a5568", marginBottom: "4px" },
  dropSub: { fontSize: "12px", color: "#a0aec0" },
  fileSelected: { display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" },
  fileIconBig: { fontSize: "36px" },
  fileName: { fontSize: "13px", fontWeight: "600", color: "#0a0f1e" },
  fileSize: { fontSize: "12px", color: "#a0aec0" },
  removeFile: { background: "#fff5f5", border: "1px solid #fed7d7", color: "#e53e3e", borderRadius: "6px", padding: "4px 10px", fontSize: "11px", cursor: "pointer", marginTop: "4px" },
  textarea: { width: "100%", border: "1.5px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "13px", fontFamily: "sans-serif", resize: "vertical", outline: "none", boxSizing: "border-box" },
  inputHint: { fontSize: "11px", color: "#a0aec0", marginTop: "5px" },
  formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" },
  field: { display: "flex", flexDirection: "column", gap: "5px" },
  label: { fontSize: "12px", fontWeight: "600", color: "#2d3748" },
  input: { padding: "10px 12px", border: "1.5px solid #e2e8f0", borderRadius: "8px", fontSize: "13px", fontFamily: "sans-serif", outline: "none", background: "#fff", color: "#0a0f1e", width: "100%", boxSizing: "border-box" },
  submitRow: { display: "flex", justifyContent: "flex-end", gap: "12px" },
  cancelBtn: { padding: "12px 24px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "14px", cursor: "pointer", color: "#4a5568" },
  submitBtn: { padding: "12px 28px", background: "#0a0f1e", color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "bold", cursor: "pointer", letterSpacing: "0.3px" },
};

export default Upload;